import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { QuizDetail, QuizAttempt, SubmitQuizInput } from '@/types';

export function useQuizDetail(quizId: number) {
  return useQuery({
    queryKey: ['quizzes', 'detail', quizId],
    queryFn: () => api.get<QuizDetail>(`/quizzes/${quizId}`),
    select: (res) => res.data,
    enabled: quizId > 0,
  });
}

export function useStartAttempt(quizId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<QuizAttempt>(`/quizzes/${quizId}/attempts`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quizzes', 'detail', quizId] });
    },
  });
}

// #32: descarta un intento sin terminar para poder empezar uno limpio.
export function useDiscardAttempt(quizId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attemptId: number) =>
      api.post<{ id: number; status: string }>(`/quizzes/${quizId}/attempts/${attemptId}/discard`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quizzes', 'detail', quizId] });
    },
  });
}

export const REFETCH_DETAIL_TIMEOUT_MS = 5000;

export function useSubmitAttempt(quizId: number, attemptId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitQuizInput) =>
      api.put<QuizAttempt>(`/quizzes/${quizId}/attempts/${attemptId}`, input),
    // Se espera el detalle recargado antes de avisar éxito: el API entrega la clave
    // (opción correcta + explicación) a quien rinde solo DESPUÉS de entregar, y la
    // pantalla de resultados se pinta con ese detalle. Sin esperar, la clave aparecería
    // un instante después.
    onSuccess: async () => {
      // Con tope: si el detalle tarda, se muestran los resultados igual (sin la clave)
      // en vez de dejar la entrega en "Enviando..." indefinidamente.
      await Promise.race([
        qc.refetchQueries({ queryKey: ['quizzes', 'detail', quizId] }),
        new Promise((resolve) => setTimeout(resolve, REFETCH_DETAIL_TIMEOUT_MS)),
      ]);
      qc.invalidateQueries({ queryKey: ['quizzes'] });
      qc.invalidateQueries({ queryKey: ['enrollments'] });
    },
  });
}
